import { useEffect, useState } from 'react';
import { MapPin, Phone, Search, User } from 'lucide-react';
import { transformDashboardData } from '../../utils/dashboardDataTransform';
import axiosInstance from '../../utils/axios.js';

const IDEAL_RANGES = {
  ph: { min: 6.5, max: 7.5 },
  organic_carbon: { min: 0.8, max: 2.0 },
  nitrogen: { min: 280, max: 560 },
  phosphorus: { min: 10, max: 25 },
  potassium: { min: 110, max: 280 },
  zinc: { min: 0.5, max: 1.0 },
  iron: { min: 5, max: 10.0 },
  boron: { min: 0.1, max: 0.5 },
};

function generateRecommendations(soilTest) {
  if (!soilTest) return [];
  const recs = [];
  
  if (soilTest.organic_carbon !== null && soilTest.organic_carbon < IDEAL_RANGES.organic_carbon.min) {
    recs.push('Apply compost or FYM to improve organic carbon');
  }
  if (soilTest.ph !== null && soilTest.ph > IDEAL_RANGES.ph.max) {
    recs.push('Apply gypsum or green manure to reduce soil pH');
  }
  if (soilTest.zinc !== null && soilTest.zinc < IDEAL_RANGES.zinc.min) {
    recs.push('Apply zinc sulfate');
  }
  if (soilTest.iron !== null && soilTest.iron < IDEAL_RANGES.iron.min) {
    recs.push('Apply iron chelate');
  }
  if (soilTest.boron !== null && soilTest.boron < IDEAL_RANGES.boron.min) {
    recs.push('Apply boron fertilizer');
  }
  if (soilTest.potassium !== null && soilTest.potassium < IDEAL_RANGES.potassium.min) {
    recs.push('Apply potash to correct K deficiency');
  }
  if (soilTest.phosphorus !== null && soilTest.phosphorus > IDEAL_RANGES.phosphorus.max) {
    recs.push('Reduce phosphorus fertilizer application');
  }
  
  if (recs.length === 0) recs.push('Soil is healthy. Maintain current practices.');
  return recs;
}

function getIssues(soilTest) {
  if (!soilTest) return [];
  const issues = [];
  
  if (soilTest.organic_carbon !== null && soilTest.organic_carbon < IDEAL_RANGES.organic_carbon.min) {
    issues.push('Low Organic Carbon');
  }
  if (soilTest.ph !== null && soilTest.ph > IDEAL_RANGES.ph.max) {
    issues.push('High pH');
  }
  if (soilTest.phosphorus !== null && soilTest.phosphorus > IDEAL_RANGES.phosphorus.max) {
    issues.push('High Phosphorus');
  }
  if (soilTest.potassium !== null && soilTest.potassium < IDEAL_RANGES.potassium.min) {
    issues.push('Low Potassium');
  }
  if (
    (soilTest.zinc !== null && soilTest.zinc < IDEAL_RANGES.zinc.min) || 
    (soilTest.iron !== null && soilTest.iron < IDEAL_RANGES.iron.min) || 
    (soilTest.boron !== null && soilTest.boron < IDEAL_RANGES.boron.min)
  ) {
    issues.push('Micronutrient Deficiency');
  }
  
  return issues;
}

export function FarmerProfile() {
  const [allData, setAllData] = useState([]);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [selectedSoilTest, setSelectedSoilTest] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/admin/dashboard-data');
      const transformedData = transformDashboardData(response.data.dashboardData || []);
      setAllData(transformedData);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.response?.data?.msg || 'Failed to load dashboard data');
      setAllData([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (selectedFarmer) {
      const data = allData.find(d => d.farmer_id === selectedFarmer.id);
      setSelectedSoilTest(data || null);
    } else {
      setSelectedSoilTest(null);
    }
  }, [selectedFarmer, allData]);

  // Create unique farmers list from the data
  const uniqueFarmersMap = new Map();
  
  allData.forEach((item) => {
    const uniqueKey = item.farmer_id; 
    if (uniqueKey && !uniqueFarmersMap.has(uniqueKey)) {
      uniqueFarmersMap.set(uniqueKey, {
        id: uniqueKey,
        name: item.farmers?.name || 'Unknown',
        phone_number: item.farmers?.phone_number || '',
        village: item.farmers?.village || '',
      });
    }
  });
  
  const farmers = Array.from(uniqueFarmersMap.values());

  const filteredFarmers = farmers.filter(f =>
    f.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.phone_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.village?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-full bg-[#F5F5F5]">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F5F5F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-white rounded-lg shadow-sm p-6">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2E7D32] text-sm"
                placeholder="Search farmer..."
              />
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredFarmers.length > 0 ? (
                filteredFarmers.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setSelectedFarmer(f)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${selectedFarmer?.id === f.id
                      ? 'bg-[#2E7D32] text-white'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <div className="font-medium">{f.name || 'Unknown'}</div>
                    {f.village && <div className="text-xs mt-1 opacity-75">{f.village}</div>}
                  </button>
                ))
              ) : (
                <div className="text-sm text-gray-500 text-center py-4">No farmers found</div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            {selectedFarmer && selectedSoilTest ? (
              <>
                <FarmerSummaryCard farmer={selectedFarmer} soilTest={selectedSoilTest} />
                <RadarChart soilTest={selectedSoilTest} />
                <NutrientBars soilTest={selectedSoilTest} />
                <AdvisoryPanel soilTest={selectedSoilTest} />
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="text-center text-gray-500 py-12">
                  {selectedFarmer ? 'No soil test data available for this farmer' : 'Select a farmer to view their profile'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FarmerSummaryCard({ farmer, soilTest }) {
  const score = soilTest.soil_health_score || 0;
  const scoreColor = score < 40 ? '#D32F2F' : score < 70 ? '#FFA000' : '#2E7D32';
  const issues = getIssues(soilTest);
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Farmer Name</p>
              <p className="font-medium text-gray-900">{farmer.name || 'Unknown'}</p>
            </div>
          </div>
          {farmer.phone_number && (
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-900">{farmer.phone_number}</p>
              </div>
            </div>
          )}
          {farmer.village && (
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Village</p>
                <p className="font-medium text-gray-900">{farmer.village}</p>
              </div>
            </div>
          )}
          {issues.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 mb-2">Key Issues</p>
              <div className="flex flex-wrap gap-2">
                {issues.map((issue, i) => (
                  <span key={i} className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full">
                    {issue}
                  </span>
                ))}
              </div>
            </div>
          )}
          {soilTest.report_id && (
            <div className="pt-4 mt-4 border-t border-gray-100">
              <a 
                href={`/reports/${soilTest.report_id}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-[#2E7D32] bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
              >
                View Original PDF Report
              </a>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center">
          <div className="relative w-32 h-32">
            <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="12"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke={scoreColor}
                strokeWidth="12"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className={`text-3xl font-bold`} style={{ color: scoreColor }}>
                {score}
              </div>
              <div className="text-sm text-gray-500">Soil Health</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RadarChart({ soilTest }) {
  const nutrients = [
    { key: 'ph', label: 'pH', unit: '' },
    { key: 'organic_carbon', label: 'OC', unit: '%' },
    { key: 'nitrogen', label: 'N', unit: 'kg/ha' },
    { key: 'phosphorus', label: 'P', unit: 'kg/ha' },
    { key: 'potassium', label: 'K', unit: 'kg/ha' },
    { key: 'zinc', label: 'Zn', unit: 'ppm' },
    { key: 'iron', label: 'Fe', unit: 'ppm' },
    { key: 'boron', label: 'B', unit: 'ppm' },
  ].slice(0, 8);

  const getStatus = (key, value) => {
    if (value === null || value === undefined) return null;
    const ideal = IDEAL_RANGES[key];
    if (!ideal) return null;
    if (value < ideal.min) return 'low';
    if (value > ideal.max) return 'high';
    return 'ideal';
  };

  const getColor = (status) => {
    if (status === 'ideal') return 'text-green-600';
    if (status === 'low') return 'text-blue-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Nutrient Overview</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {nutrients.map(nutrient => {
          const value = soilTest[nutrient.key];
          const status = getStatus(nutrient.key, value);
          const ideal = IDEAL_RANGES[nutrient.key];

          return (
            <div key={nutrient.key} className="text-center">
              <div className={`text-2xl font-bold ${getColor(status)}`}>
                {value !== null && value !== undefined ? value.toFixed(1) : '-'}
              </div>
              <div className="text-sm text-gray-600 mt-1">{nutrient.label}</div>
              {ideal && (
                <div className="text-xs text-gray-500 mt-1">
                  Ideal: {ideal.min}-{ideal.max}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NutrientBars({ soilTest }) {
  const nutrients = [
    { key: 'ph', label: 'pH', unit: '' },
    { key: 'organic_carbon', label: 'Organic Carbon', unit: '%' },
    { key: 'nitrogen', label: 'Nitrogen (N)', unit: 'kg/ha' },
    { key: 'phosphorus', label: 'Phosphorus (P)', unit: 'kg/ha' },
    { key: 'potassium', label: 'Potassium (K)', unit: 'kg/ha' },
  ];

  const getBarPosition = (key, value) => {
  if (value === null || value === undefined) return 50;

  const ideal = IDEAL_RANGES[key];
  if (!ideal) return 50;

  const range = ideal.max - ideal.min;

  const scaleMin = ideal.min - range * 2;
  const scaleMax = ideal.max + range * 2;

  const position =
    ((value - scaleMin) / (scaleMax - scaleMin)) * 100;

  return Math.max(0, Math.min(100, position));
};

  const getIdealRangeStyle = key => {
  const ideal = IDEAL_RANGES[key];

  const range = ideal.max - ideal.min;

  const scaleMin = ideal.min - range * 2;
  const scaleMax = ideal.max + range * 2;

  return {
    left: `${((ideal.min - scaleMin) / (scaleMax - scaleMin)) * 100}%`,
    width: `${((ideal.max - ideal.min) / (scaleMax - scaleMin)) * 100}%`,
  };
};

  const getBarColor = (key, value) => {
    const status = getStatus(key, value);
    if (status === 'ideal') return '#2E7D32';
    if (status === 'low') return '#2196F3';
    return '#D32F2F';
  };

  function getStatus(key, value) {
    if (value === null || value === undefined) return null;
    const ideal = IDEAL_RANGES[key];
    if (!ideal) return null;
    if (value < ideal.min) return 'low';
    if (value > ideal.max) return 'high';
    return 'ideal';
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Nutrient Levels</h3>
      <div className="space-y-4">
        {nutrients.map(nutrient => {
          const value = soilTest[nutrient.key];
          const ideal = IDEAL_RANGES[nutrient.key];
          const position = getBarPosition(nutrient.key, value);

          return (
            <div key={nutrient.key} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700 font-medium">{nutrient.label}</span>
                <span
                  className="font-semibold"
                  style={{
                    color: getBarColor(nutrient.key, value)
                  }}
                >
                  {value.toFixed(1)} {nutrient.unit}
                </span>
              </div>
              {ideal && position !== null ? (
                <div className="space-y-2">

                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Low</span>
                    <span>Ideal</span>
                    <span>High</span>
                  </div>

                  <div className="relative">

                    {/* Pointer */}
                    <div
                      className="absolute -top-5 text-sm font-bold transition-all"
                      style={{
                        left: `calc(${position}% - 6px)`,
                        color: getBarColor(nutrient.key, value)
                      }}
                    >
                      ▼
                    </div>

                    {/* Background */}
                    <div className="relative h-3 bg-gray-200 rounded-full">

                      {/* Ideal range */}
                      <div
                        className="absolute h-3 rounded-full bg-green-500"
                        style={getIdealRangeStyle(nutrient.key)}
                      />

                    </div>

                  </div>

                  <div className="text-xs text-gray-500">
                    Ideal: {ideal.min} – {ideal.max} {nutrient.unit}
                  </div>

                </div>
              ) : (
                <div className="h-8 bg-gray-200 rounded-lg" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdvisoryPanel({ soilTest }) {
  const recommendations = generateRecommendations(soilTest);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Advisory & Recommendations</h3>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="font-semibold text-gray-900 mb-2">Summary</p>
        <p className="text-sm text-gray-700">
          Based on the soil test results, the following recommendations are provided to improve soil health and crop productivity.
        </p>
      </div>

      <div className="space-y-3">
        {recommendations.map((rec, i) => (
          <div key={i} className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#2E7D32] text-white flex items-center justify-center text-sm font-medium">
              {i + 1}
            </div>
            <p className="text-sm text-gray-700 flex-1">{rec}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
